import React, { useEffect, useMemo, useState } from 'react';
import { Modal, FormLayout, TextField, InlineStack, Badge, Text, BlockStack } from '@shopify/polaris';
import { configApi } from '../../../services/api/configApi';

export function WayfairAgentCredentialsModal({ open, onClose, onSave, config = {} }) {
  const [formData, setFormData] = useState({
    connectionName: 'Wayfair Agent',
    baseUrl: '',
    apiKey: '',
    supplierKey: '',
    testPath: '/api/v1/runs'
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState({ ok: null, message: '' });

  useEffect(() => {
    if (!open) return;
    setFormData({
      connectionName: config.connectionName || 'Wayfair Agent',
      baseUrl: config.baseUrl || '',
      apiKey: config.apiKey || config.token || '',
      supplierKey: config.supplierKey || '',
      testPath: config.testPath || '/api/v1/runs'
    });
    setStatus({ ok: null, message: '' });
  }, [open, config]);

  const canSave = useMemo(() => (
    !!formData.connectionName.trim() &&
    !!formData.baseUrl.trim() &&
    !!formData.apiKey.trim() &&
    !!formData.supplierKey.trim()
  ), [formData]);

  const payload = () => ({
    connectionName: formData.connectionName.trim(),
    baseUrl: formData.baseUrl.trim().replace(/\/$/, ''),
    apiKey: formData.apiKey === '****' ? undefined : formData.apiKey,
    supplierKey: formData.supplierKey.trim(),
    testPath: formData.testPath.trim() || '/api/v1/runs'
  });

  const handleSave = async () => {
    setSaving(true);
    setStatus({ ok: null, message: '' });
    try {
      await configApi.saveBearerCredentials('wayfairagent', payload());
      setStatus({ ok: true, message: 'Connection saved' });
      if (onSave) await onSave();
      onClose();
    } catch (error) {
      setStatus({ ok: false, message: error.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setStatus({ ok: null, message: 'Testing connection...' });
    try {
      const result = await configApi.validateCredentials('wayfairagent', {
        authMethod: 'bearer',
        ...payload()
      });
      if (result.success && result.valid) {
        setStatus({ ok: true, message: result.message || 'Connection successful' });
      } else {
        setStatus({ ok: false, message: result.error || 'Connection failed' });
      }
    } catch (error) {
      setStatus({ ok: false, message: error.message || 'Connection failed' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Wayfair Agent Connection"
      primaryAction={{
        content: 'Save',
        onAction: handleSave,
        disabled: !canSave,
        loading: saving
      }}
      secondaryActions={[
        { content: 'Test connection', onAction: handleTest, disabled: !canSave || testing, loading: testing },
        { content: 'Cancel', onAction: onClose }
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <FormLayout>
            <TextField
              label="Connection name"
              value={formData.connectionName}
              onChange={(value) => setFormData(prev => ({ ...prev, connectionName: value }))}
              autoComplete="off"
            />
            <TextField
              label="Wayfair Agent URL"
              value={formData.baseUrl}
              onChange={(value) => setFormData(prev => ({ ...prev, baseUrl: value }))}
              autoComplete="off"
            />
            <TextField
              label="Wayfair auth API key"
              value={formData.apiKey}
              onChange={(value) => setFormData(prev => ({ ...prev, apiKey: value }))}
              type="password"
              autoComplete="off"
              helpText={config.apiKey === '****' || config.token === '****' ? 'Stored; leave as **** to keep unchanged' : ''}
            />
            <TextField
              label="Supplier key"
              value={formData.supplierKey}
              onChange={(value) => setFormData(prev => ({ ...prev, supplierKey: value }))}
              autoComplete="off"
            />
            <TextField
              label="Test path"
              value={formData.testPath}
              onChange={(value) => setFormData(prev => ({ ...prev, testPath: value }))}
              autoComplete="off"
            />
          </FormLayout>

          {status.message && (
            <InlineStack gap="200">
              <Badge tone={status.ok ? 'success' : status.ok === false ? 'critical' : 'info'}>
                {status.ok ? 'Connected' : status.ok === false ? 'Failed' : 'Testing'}
              </Badge>
              <Text as="span">{status.message}</Text>
            </InlineStack>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

export default WayfairAgentCredentialsModal;
